import React, { useState, useEffect } from 'react';
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import { TrustJetPlane, getAircraftBundledCarouselUrls } from '@assets/images';

/**
 * Image carousel that pre-validates URLs. Only images that load successfully
 * become carousel slides; the counter reflects the valid count (e.g. 2/2
 * when 4 of 6 URLs are broken). If none load, a single fallback is shown
 * with no carousel controls.
 *
 * Fallback when all images are invalid:
 *   1. Bundled make_model image (from aircraft.make_model), if provided
 *   2. TrustJetPlane placeholder
 *
 * Pass `aircraft` to enable the make_model fallback.
 */

const PROBE_TIMEOUT_MS = 5000;

// Test if a URL loads as an image. Resolves to the URL on success, null on
// error or timeout. new Image() requests don't need CORS, and successful
// probes prime the browser cache so the subsequent <img> render is instant.
const probeImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new window.Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      resolve(ok ? src : null);
    };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    setTimeout(() => finish(false), PROBE_TIMEOUT_MS);
    img.src = src;
  });

const ImageCarousel = ({ images = [], aircraft = null, alt = 'Image', className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // null while probing, array once resolved
  const [validImages, setValidImages] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Key the effect on the URL contents (not the array reference) — parents
  // like TailDetailsDrawer recreate the array via .map() on every render,
  // which would otherwise restart probing and flash the spinner each time.
  const imagesKey = Array.isArray(images) ? images.join('|') : '';

  const makeModel =
    aircraft?.aircraft_make_model?.make_model ||
    aircraft?.make_model ||
    aircraft?.makeModel;

  useEffect(() => {
    setCurrentIndex(0);
    setIsLoading(true);
    let cancelled = false;
    const run = async () => {
      // Probe every DB URL, keep only the ones that load.
      const primary = Array.isArray(images) ? images : [];
      const probed = await Promise.all(primary.map(probeImage));
      const valid = probed.filter(Boolean);
      if (cancelled) return;
      if (valid.length > 0) {
        setValidImages(valid);
        return;
      }
      // Every primary URL failed — swap the whole set for the make_model
      // bundle so the carousel isn't reduced to a single static slide.
      const bundled = getAircraftBundledCarouselUrls(makeModel);
      if (bundled.length === 0) {
        setValidImages([]);
        return;
      }
      // Bundled images live in public/, they should always load — but probe
      // anyway to stay defensive (bad deploy, missing file, etc).
      const bundledOk = (await Promise.all(bundled.map(probeImage))).filter(Boolean);
      if (!cancelled) setValidImages(bundledOk);
    };
    setValidImages(null);
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesKey, makeModel]);

  if (!images || images.length === 0) {
    return null;
  }

  // Probing — show spinner in the card frame
  if (validImages === null) {
    return (
      <div className={`relative bg-gray-100 rounded-t-2xl ${className}`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
        </div>
      </div>
    );
  }

  // Neither DB URLs nor bundled make_model images loaded — last-resort placeholder.
  if (validImages.length === 0) {
    return (
      <div className={`relative ${className}`}>
        <img
          src={TrustJetPlane}
          alt={alt}
          className="w-full h-full rounded-t-2xl object-cover"
        />
      </div>
    );
  }

  const total = validImages.length;

  const goToPrevious = (e) => {
    e.stopPropagation();
    setIsLoading(true);
    setCurrentIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setIsLoading(true);
    setCurrentIndex((prev) => (prev === total - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setIsLoading(true);
    setCurrentIndex(index);
  };

  return (
    <div className={`relative group ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-t-2xl z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}

      <img
        src={validImages[currentIndex]}
        alt={`${alt} ${currentIndex + 1}`}
        className="w-full h-full rounded-t-2xl object-cover"
        onLoad={() => setIsLoading(false)}
      />

      {total > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <RiArrowLeftSLine className="w-6 h-6 text-gray-700" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <RiArrowRightSLine className="w-6 h-6 text-gray-700" />
          </button>
        </>
      )}

      {total > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {validImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-4'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      )}

      {total > 1 && (
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
          {currentIndex + 1} / {total}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
